import React, { useState, useEffect } from 'react';
import { 
  Send, 
  Users, 
  Bell, 
  Search, 
  CheckCircle2, 
  XCircle,
  Clock,
  ChevronDown,
  RefreshCw,
  Smartphone,
  MessageSquare,
  Settings,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../services/api';

const PushNotificationsPage = () => {
  const [activeTab, setActiveTab] = useState('send'); // send, history, settings
  const [loading, setLoading] = useState(false);
  const [passengers, setPassengers] = useState([]);
  const [selectedPassengers, setSelectedPassengers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHasToken, setFilterHasToken] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('selected'); // all or selected
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Settings state
  const [pushSettings, setPushSettings] = useState({
    use_companion_api: false,
    companion_api_url: '',
    companion_admin_key: '',
    expo_project_id: ''
  });
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchPassengers();
    fetchCampaigns();
    fetchStats();
    fetchPushSettings();
  }, []);

  const fetchPushSettings = async () => {
    try {
      const response = await api.get('/admin/push/settings');
      setPushSettings(response.data);
    } catch (err) {
      console.error('Error fetching push settings:', err);
    }
  };

  const savePushSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/push/settings', pushSettings);
      alert('Settings u ruajtën me sukses!');
    } catch (err) {
      alert('Gabim gjatë ruajtjes: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchPassengers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterHasToken !== null) params.append('has_token', filterHasToken);
      params.append('limit', '200');
      
      const response = await api.get(`/admin/push/passengers?${params.toString()}`);
      setPassengers(response.data);
    } catch (err) {
      console.error('Error fetching passengers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/admin/push/campaigns?limit=50');
      setCampaigns(response.data);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/push/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPassengers();
  };

  const togglePassengerSelection = (passengerId) => {
    setSelectedPassengers(prev => 
      prev.includes(passengerId)
        ? prev.filter(id => id !== passengerId)
        : [...prev, passengerId]
    );
  };

  const selectAll = () => {
    const passengersWithToken = passengers.filter(p => p.has_push_token);
    setSelectedPassengers(passengersWithToken.map(p => p.id));
  };

  const deselectAll = () => {
    setSelectedPassengers([]);
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Titulli dhe mesazhi janë të detyrueshëm');
      return;
    }

    if (target === 'selected' && selectedPassengers.length === 0) {
      alert('Zgjidhni të paktën një pasagjer');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await api.post('/admin/push/send', {
        title: title.trim(),
        body: body.trim(),
        target,
        passenger_ids: target === 'selected' ? selectedPassengers : []
      });

      setResult({
        success: true,
        message: response.data.message,
        sent: response.data.sent_count,
        failed: response.data.failed_count
      });

      // Reset form
      setTitle('');
      setBody('');
      setSelectedPassengers([]);
      
      // Refresh campaigns
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      setResult({
        success: false,
        message: err.response?.data?.detail || 'Ndodhi një gabim'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bell className="w-7 h-7 text-red-600" />
          Push Notifications
        </h1>
        <p className="text-gray-600 mt-1">
          Dërgo njoftime te aplikacioni i pasagjerëve
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pasagjerë me App</p>
                <p className="text-xl font-bold">{stats.passengers.with_token} / {stats.passengers.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Smartphone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Shoferë me App</p>
                <p className="text-xl font-bold">{stats.drivers.with_token} / {stats.drivers.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Kampanja Totale</p>
                <p className="text-xl font-bold">{stats.campaigns.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Send className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Njoftime Dërguar</p>
                <p className="text-xl font-bold">{stats.campaigns.total_sent}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border mb-6">
        <div className="border-b">
          <nav className="flex">
            {[
              { id: 'send', label: 'Dërgo Njoftim', icon: Send },
              { id: 'history', label: 'Historiku', icon: Clock },
              { id: 'settings', label: 'Konfigurime', icon: Settings },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'send' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Form */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Përmbajtja e Njoftimit</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titulli *
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="p.sh. Ofertë e re!"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      maxLength={100}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mesazhi *
                    </label>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Shkruani mesazhin e njoftimit..."
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">{body.length}/500 karaktere</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dërgo te
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="target"
                          value="selected"
                          checked={target === 'selected'}
                          onChange={() => setTarget('selected')}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span>Pasagjerë të zgjedhur</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="target"
                          value="all"
                          checked={target === 'all'}
                          onChange={() => setTarget('all')}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span>Të gjithë pasagjerët</span>
                      </label>
                    </div>
                  </div>

                  {result && (
                    <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                      <div className="flex items-center gap-2">
                        {result.success ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-medium">{result.message}</span>
                      </div>
                      {result.success && (
                        <p className="text-sm mt-1">
                          Dërguar: {result.sent} | Dështuar: {result.failed}
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleSendNotification}
                    disabled={sending || !title.trim() || !body.trim()}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {sending ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Duke dërguar...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Dërgo Njoftimin
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Right: Passenger Selection */}
              {target === 'selected' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Zgjidh Pasagjerët</h3>
                    <span className="text-sm text-gray-500">
                      {selectedPassengers.length} të zgjedhur
                    </span>
                  </div>

                  {/* Search & Filters */}
                  <div className="flex gap-2 mb-4">
                    <form onSubmit={handleSearch} className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Kërko emër ose telefon..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </form>
                    <select
                      value={filterHasToken === null ? '' : filterHasToken.toString()}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFilterHasToken(val === '' ? null : val === 'true');
                        setTimeout(fetchPassengers, 100);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">Të gjithë</option>
                      <option value="true">Me app</option>
                      <option value="false">Pa app</option>
                    </select>
                  </div>

                  {/* Select All / Deselect */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={selectAll}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Zgjidh të gjithë me app
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={deselectAll}
                      className="text-sm text-gray-600 hover:text-gray-700"
                    >
                      Hiq zgjedhjen
                    </button>
                  </div>

                  {/* Passenger List */}
                  <div className="border rounded-lg max-h-96 overflow-y-auto">
                    {loading ? (
                      <div className="p-8 text-center text-gray-500">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Duke ngarkuar...
                      </div>
                    ) : passengers.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        Asnjë pasagjer nuk u gjet
                      </div>
                    ) : (
                      passengers.map(passenger => (
                        <div
                          key={passenger.id}
                          onClick={() => passenger.has_push_token && togglePassengerSelection(passenger.id)}
                          className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                            !passenger.has_push_token ? 'opacity-50 cursor-not-allowed' : ''
                          } ${selectedPassengers.includes(passenger.id) ? 'bg-red-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPassengers.includes(passenger.id)}
                            onChange={() => {}}
                            disabled={!passenger.has_push_token}
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{passenger.name}</p>
                            <p className="text-sm text-gray-500">{passenger.phone}</p>
                          </div>
                          {passenger.has_push_token ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              Me app
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                              Pa app
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Historiku i Kampanjave</h3>
                <button
                  onClick={fetchCampaigns}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <RefreshCw className="w-4 h-4" />
                  Rifresko
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titulli</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Dërguar</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Dështuar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dërgues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {campaigns.map(campaign => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(campaign.created_at).toLocaleDateString('sq-AL', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{campaign.title}</p>
                          <p className="text-sm text-gray-500 truncate max-w-xs">{campaign.body}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {campaign.target === 'all' ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                              Të gjithë
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                              Të zgjedhur
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-green-600 font-medium">{campaign.sent_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={campaign.failed_count > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>
                            {campaign.failed_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {campaign.sent_by_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {campaigns.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    Asnjë kampanjë ende
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <h3 className="text-lg font-semibold mb-4">Konfigurime Push Notifications</h3>
              
              {/* Companion API Toggle */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Përdor Companion API</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Dërgo njoftimet te backend-i i Passenger App në vend të Expo direkt
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pushSettings.use_companion_api}
                      onChange={(e) => setPushSettings({
                        ...pushSettings,
                        use_companion_api: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>

              {/* Companion API Settings */}
              {pushSettings.use_companion_api && (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Companion API URL *
                    </label>
                    <input
                      type="url"
                      value={pushSettings.companion_api_url}
                      onChange={(e) => setPushSettings({
                        ...pushSettings,
                        companion_api_url: e.target.value
                      })}
                      placeholder="https://passenger-app.example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      URL-ja bazë e backend-it të Passenger App
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Admin Key (X-Admin-Key) *
                    </label>
                    <div className="relative">
                      <input
                        type={showAdminKey ? 'text' : 'password'}
                        value={pushSettings.companion_admin_key}
                        onChange={(e) => setPushSettings({
                          ...pushSettings,
                          companion_admin_key: e.target.value
                        })}
                        placeholder="your_secret_admin_key"
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdminKey(!showAdminKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showAdminKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Çelësi sekret për autentikim me Passenger App backend
                    </p>
                  </div>
                </div>
              )}

              {/* Expo Project ID */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expo Project ID
                </label>
                <input
                  type="text"
                  value={pushSettings.expo_project_id}
                  onChange={(e) => setPushSettings({
                    ...pushSettings,
                    expo_project_id: e.target.value
                  })}
                  placeholder="your-expo-project-id"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID e projektit Expo (gjendet në app.json ose Expo dashboard)
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h5 className="font-medium text-blue-800 mb-2">Si funksionon?</h5>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Direct Expo:</strong> Njoftimet dërgohen direkt te Expo Push API</li>
                  <li>• <strong>Companion API:</strong> Njoftimet dërgohen te Passenger App backend, i cili i përcjell te Expo</li>
                </ul>
                <p className="text-sm text-blue-700 mt-2">
                  Companion API rekomandohet kur Passenger App ka backend të veçantë.
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={savePushSettings}
                disabled={savingSettings}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 transition-colors"
              >
                {savingSettings ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Duke ruajtur...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Ruaj Settings
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushNotificationsPage;
